/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { HTMLAttributes, useCallback, useState } from 'preact/compat';

import {
  Accordion,
  AccordionSection,
  Button,
  CartItem,
  CartList,
  ImageProps,
  Price,
} from '@adobe-commerce/elsie/components';
import { useText } from '@adobe-commerce/elsie/i18n';
import { ChevronDown, ChevronRight } from '@adobe-commerce/elsie/icons';
import { Container, Slot, SlotProps, classes } from '@adobe-commerce/elsie/lib';

import { QuoteSummaryList as QuoteSummaryListComponent } from '@/quote-management/components';
import {
  CartItemModel as NegotiableQuoteItemModel,
  QuoteDisplayAmount,
} from '@/quote-management/data/models';
import { state } from '@/quote-management/lib/state';
import { useQuoteSummaryData } from '@/quote-management/hooks/useQuoteSummaryData';
import {
  getPriceProps,
  getSavingsAmount,
  getTotalPriceProps,
} from '@/quote-management/lib/priceCalculators';
import {
  getImage,
  getTitle,
  getSku,
  getProductAttributes,
  getConfigurations,
  getTotalExcludingTax,
} from '@/quote-management/lib/itemFormatters';

export interface QuoteSummaryListProps extends HTMLAttributes<HTMLDivElement> {
  hideHeading?: boolean;
  hideFooter?: boolean;
  routeProduct?: (item: NegotiableQuoteItemModel) => string;
  showMaxItems?: boolean;
  attributesToHide?: SwitchableAttributes[];
  slots?: {
    Heading?: SlotProps<{ count: number; quoteId: string }>;
    Footer?: SlotProps<{ item: NegotiableQuoteItemModel }>;
    Thumbnail?: SlotProps<{
      item: NegotiableQuoteItemModel;
      defaultImageProps: ImageProps;
    }>;
    ProductAttributes?: SlotProps<{ item: NegotiableQuoteItemModel }>;
    QuoteSummaryFooter?: SlotProps<{
      displayMaxItems: boolean;
    }>;
    QuoteItem?: SlotProps;
    ItemTitle?: SlotProps<{ item: NegotiableQuoteItemModel }>;
    ItemPrice?: SlotProps<{ item: NegotiableQuoteItemModel }>;
    ItemTotal?: SlotProps<{ item: NegotiableQuoteItemModel }>;
    ItemSku?: SlotProps<{ item: NegotiableQuoteItemModel }>;
  };
  accordion?: boolean;
  variant?: 'primary' | 'secondary';
  showDiscount?: boolean;
  showSavings?: boolean;
}

// Attributes that can be hidden or shown
export type SwitchableAttributes =
  | 'name'
  | 'image'
  | 'configurations'
  | 'warning'
  | 'alert'
  | 'sku'
  | 'price'
  | 'quantity'
  | 'total'
  | 'totalDiscount'
  | 'totalExcludingTax';

export const QuoteSummaryList: Container<QuoteSummaryListProps> = ({
  hideHeading,
  hideFooter,
  routeProduct,
  showMaxItems = true,
  attributesToHide = [],
  slots,
  accordion = false,
  variant = 'primary',
  showDiscount,
  showSavings,
  ...props
}) => {
  const CART_SUMMARY_MAX_ITEMS = 5;

  const { quoteData, loading } = useQuoteSummaryData();
  const [displayMaxItems, setDisplayMaxItems] = useState<boolean>(showMaxItems);

  const { quoteDisplaySettings, quoteSummaryMaxItems } = state.config;
  const maxItems = quoteSummaryMaxItems || CART_SUMMARY_MAX_ITEMS;

  const dictionary = useText({
    discountedPrice: 'NegotiableQuote.QuoteSummaryList.discountedPrice',
    discountPercentage: 'NegotiableQuote.QuoteSummaryList.discountPercentage',
    editQuote: 'NegotiableQuote.QuoteSummaryList.editQuote',
    file: 'NegotiableQuote.QuoteSummaryList.file',
    files: 'NegotiableQuote.QuoteSummaryList.files',
    heading: 'NegotiableQuote.QuoteSummaryList.heading',
    listOfQuoteItems: 'NegotiableQuote.QuoteSummaryList.listOfQuoteItems',
    regularPrice: 'NegotiableQuote.QuoteSummaryList.regularPrice',
    savingsAmount: 'NegotiableQuote.QuoteSummaryList.savingsAmount',
    viewMore: 'NegotiableQuote.QuoteSummaryList.viewMore',
  });

  // Display max items
  const handleMaxItemsView = useCallback(() => {
    setDisplayMaxItems((prev) => !prev);
  }, []);

  // Options for item formatters
  const formatterOptions = {
    attributesToHide,
    routeProduct,
    slots,
    dictionary,
    quoteDisplaySettings,
  };

  // Options for price calculators
  const calculatorOptions = {
    dictionary,
    quoteDisplaySettings,
  };

  const getFooter = (item: NegotiableQuoteItemModel) => {
    return <Slot name="Footer" slot={slots?.Footer} context={{ item }} />;
  };

  const getCartItemEntries = () => {
    return quoteData?.totalQuantity
      ? quoteData.items.map((item, index) => {
          const { totalProps, discountProps } = getTotalPriceProps(
            item,
            calculatorOptions
          );

          const defaultCartItem = (
            <CartItem
              loading={loading}
              key={item.uid}
              data-testid={`quote-summary-list-item-entry-${item.uid}`}
              image={getImage(item, index, formatterOptions)}
              title={
                <Slot
                  name="ItemTitle"
                  slot={slots?.ItemTitle}
                  context={{ item }}
                >
                  {getTitle(item, formatterOptions)}
                </Slot>
              }
              sku={
                attributesToHide.includes('sku') ? undefined : (
                  <Slot name="ItemSku" slot={slots?.ItemSku} context={{ item }}>
                    {getSku(item)}
                  </Slot>
                )
              }
              price={
                attributesToHide.includes('price') ? undefined : (
                  <Slot
                    name="ItemPrice"
                    slot={slots?.ItemPrice}
                    context={{ item }}
                    slotTag="span"
                  >
                    <Price {...getPriceProps(item, calculatorOptions)} />
                  </Slot>
                )
              }
              quantity={
                attributesToHide.includes('quantity')
                  ? undefined
                  : item.quantity
              }
              total={
                <>
                  {attributesToHide.includes('total') ? undefined : (
                    <Slot
                      name="ItemTotal"
                      slot={slots?.ItemTotal}
                      context={{ item }}
                    >
                      <Price {...totalProps} />
                    </Slot>
                  )}
                  {attributesToHide.includes('totalDiscount')
                    ? undefined
                    : discountProps && (
                        <Slot
                          name="ItemTotal"
                          slot={slots?.ItemTotal}
                          context={{ item }}
                        >
                          <Price {...discountProps} />
                        </Slot>
                      )}
                </>
              }
              attributes={getProductAttributes(item, formatterOptions)}
              configurations={getConfigurations(item, formatterOptions)}
              totalExcludingTax={
                attributesToHide.includes('totalExcludingTax')
                  ? undefined
                  : getTotalExcludingTax(item, formatterOptions)
              }
              taxIncluded={
                quoteDisplaySettings?.price === QuoteDisplayAmount.TAX_INCLUDED
              }
              taxExcluded={
                !attributesToHide.includes('totalExcludingTax') &&
                quoteDisplaySettings?.price ===
                  QuoteDisplayAmount.TAX_INCLUDED_AND_EXCLUDED
              }
              discount={
                showDiscount &&
                item.discounted &&
                item.discountPercentage !== undefined ? (
                  <div data-testid="item-discount-percentage">
                    {dictionary.discountPercentage.replace(
                      '{discount}',
                      item.discountPercentage.toString()
                    )}
                  </div>
                ) : undefined
              }
              savings={
                showSavings &&
                item.discounted &&
                item.savingsAmount !== undefined ? (
                  <div>
                    <span>
                      <Price {...getSavingsAmount(item)} />
                    </span>
                    &nbsp;
                    {dictionary.savingsAmount}
                  </div>
                ) : undefined
              }
              footer={getFooter(item)}
            />
          );

          return (
            <Slot
              key={item.uid}
              name="CartItem"
              slot={slots?.QuoteItem}
              context={{
                item,
                index,
                loading,
                attributesToHide,
                quoteDisplaySettings,
                showDiscount,
                showSavings,
                dictionary,
                getImage,
                getTitle,
                getSku,
                getPriceProps,
                getTotalPriceProps,
                getProductAttributes,
                getConfigurations,
                getTotalExcludingTax,
                getFooter,
                getSavingsAmount,
              }}
            >
              {defaultCartItem}
            </Slot>
          );
        })
      : null;
  };

  const heading = (
    <Slot
      name="Heading"
      slot={slots?.Heading}
      context={{
        count: quoteData?.totalQuantity ?? 0,
        quoteId: quoteData?.uid ?? '',
      }}
    >
      <div data-testid="default-quote-summary-list-heading">
        {dictionary.heading.replace(
          '({count})',
          quoteData?.totalQuantity
            ? `(${quoteData?.totalQuantity.toString()})`
            : ''
        )}
      </div>
    </Slot>
  );

  // Heading Section text for screen reader
  const headingSection = heading.props.children.props.children;

  // Main Product Entries
  const mainProductEntries = getCartItemEntries();

  const totalLineItems = quoteData?.items.length || 0;
  const numOfVisibleItems = displayMaxItems
    ? Math.min(totalLineItems, maxItems)
    : totalLineItems;

  const hasHiddenItems = totalLineItems > numOfVisibleItems;

  const footerActions =
    quoteData?.totalQuantity && mainProductEntries ? (
      <Slot
        name="Footer"
        slot={slots?.QuoteSummaryFooter}
        context={{ displayMaxItems }}
        data-testid="quote-summary-list-footer-slot"
        className="quote-management-quote-summary-list-footer"
      >
        {hasHiddenItems ? (
          <Button
            className="quote-management-quote-summary-list-footer__action"
            onClick={handleMaxItemsView}
            data-testid="view-more-items-button"
            variant="tertiary"
          >
            {dictionary.viewMore}
          </Button>
        ) : null}
      </Slot>
    ) : null;

  const productEntries = quoteData?.totalQuantity ? (
    <CartList data-testid="quote-summary-list">
      {mainProductEntries?.slice(
        0,
        displayMaxItems ? Math.min(totalLineItems, maxItems) : totalLineItems
      )}
    </CartList>
  ) : null;

  return accordion ? (
    <Accordion
      data-testid="quote-summary-list-accordion"
      className={classes([
        `quote-management-quote-summary-list-accordion`,
        `quote-management-quote-summary-list__background--${variant}`,
      ])}
      iconOpen={ChevronRight}
      iconClose={ChevronDown}
    >
      <AccordionSection
        title={headingSection}
        ariaLabelTitle={headingSection}
        data-testid="quote-summary-list-accordion__section"
        open={true}
        renderContentWhenClosed={true}
      >
        <QuoteSummaryListComponent
          {...props}
          aria-expanded={true}
          aria-label={dictionary.listOfQuoteItems}
          className={`quote-management-quote-summary-list-accordion__list`}
          footer={hideFooter ? undefined : footerActions}
          products={productEntries}
          outOfStockMessage={undefined}
          loading={loading}
          variant={variant}
        />
      </AccordionSection>
    </Accordion>
  ) : (
    <QuoteSummaryListComponent
      {...props}
      heading={hideHeading ? undefined : heading}
      footer={hideFooter ? undefined : footerActions}
      products={productEntries}
      outOfStockMessage={undefined}
      loading={loading}
      variant={variant}
    />
  );
};
